export type ExtractionMode = 'simple' | 'css' | 'xpath' | 'json';

interface ExtractPayload {
  url: string;
  request?: string;
  prompt?: string;
  mode?: ExtractionMode;
  selector?: string;
  includeScreenshot?: boolean;
  waitForSelector?: string;
  waitTime?: number;
  captchaToken?: string;
  options?: {
    wait_ms?: number;
    wait_until?: 'load' | 'domcontentloaded' | 'networkidle';
    wait_for_selector?: string;
    screenshot?: boolean;
    headers?: Record<string, string>;
    cookies?: string;
    timeout_ms?: number;
  };
  idempotency_key?: string;
}

export interface ExtractRunSession {
  runId: string;
  status?: string;
  step?: string | null;
  statusUrl?: string;
  websocketPath?: string;
  websocketUrl?: string;
  websocketToken?: string;
  websocketTokenExpiresAt?: number;
  json?: Record<string, unknown> | null;
  screenshotUrl?: string | null;
  error?: string | null;
  retryable?: boolean;
  startedAt?: string;
  finishedAt?: string;
}

export class ApiError extends Error {
  status?: number;
  details?: unknown;

  constructor(message: string, status?: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

const GENERIC_EXTRACTION_ERROR_MESSAGE = 'Failed to extract data. Please try again.';
const SAFE_SERVER_ERROR_MESSAGES = new Set([
  'Please create a free account to continue.',
]);
const TERMINAL_EXTRACT_STATUSES = new Set([
  'success',
  'failed',
  'cancelled',
  'completed',
  'completed_partial',
]);
const ERROR_EXTRACT_STATUSES = new Set(['failed', 'cancelled']);

function getErrorMessageCandidate(candidate: unknown): string | null {
  if (typeof candidate === 'string') {
    return candidate.trim().length > 0 ? candidate.trim() : null;
  }

  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    return null;
  }

  const record = candidate as Record<string, unknown>;

  return getErrorMessageCandidate(record.message) ?? getErrorMessageCandidate(record.error);
}

export function getErrorMessageFromBody(parsed: unknown): string | null {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null;
  }

  const record = parsed as Record<string, unknown>;
  const candidates = [record.error, record.message];

  for (const candidate of candidates) {
    const message = getErrorMessageCandidate(candidate);
    if (message) {
      return message;
    }
  }

  return null;
}

function getRunIdFromBody(data: Record<string, unknown>): string | null {
  return typeof data.run_id === 'string'
    ? data.run_id
    : typeof data.job_id === 'string'
      ? data.job_id
      : typeof data.runId === 'string'
        ? data.runId
        : typeof data.jobId === 'string'
          ? data.jobId
          : null;
}

function normalizeExtractRunSession(data: Record<string, unknown>): ExtractRunSession {
  const runId = getRunIdFromBody(data);
  if (!runId) {
    throw new ApiError('В ответе отсутствует run_id', 500, data);
  }

  const status = typeof data.status === 'string' ? data.status : undefined;

  const nestedData = data.data;
  const jsonPayload =
    nestedData && typeof nestedData === 'object' && !Array.isArray(nestedData)
      ? (nestedData as Record<string, unknown>)
      : data.json && typeof data.json === 'object' && !Array.isArray(data.json)
        ? (data.json as Record<string, unknown>)
        : null;

  const screenshotUrlRaw = data.screenshot_url ?? data.screenshotUrl;
  const errorMessage = isTerminalExtractErrorStatus(status) ? getErrorMessageFromBody(data) : null;

  return {
    runId,
    status,
    step: typeof data.step === 'string' ? data.step : null,
    statusUrl: typeof data.status_url === 'string' ? data.status_url : typeof data.statusUrl === 'string' ? data.statusUrl : undefined,
    websocketPath:
      typeof data.websocket === 'string'
        ? data.websocket
        : typeof data.websocket_path === 'string'
          ? data.websocket_path
          : typeof data.websocketPath === 'string'
            ? data.websocketPath
            : undefined,
    websocketUrl:
      typeof data.websocket_url === 'string'
        ? data.websocket_url
        : typeof data.websocketUrl === 'string'
          ? data.websocketUrl
          : undefined,
    websocketToken:
      typeof data.websocket_token === 'string'
        ? data.websocket_token
        : typeof data.websocketToken === 'string'
          ? data.websocketToken
          : undefined,
    websocketTokenExpiresAt:
      typeof data.websocket_token_expires_at === 'number'
        ? data.websocket_token_expires_at
        : typeof data.websocketTokenExpiresAt === 'number'
          ? data.websocketTokenExpiresAt
          : undefined,
    json: jsonPayload,
    screenshotUrl: typeof screenshotUrlRaw === 'string' ? screenshotUrlRaw : null,
    error: errorMessage,
    retryable: typeof data.retryable === 'boolean' ? data.retryable : undefined,
    startedAt: typeof data.started_at === 'string' ? data.started_at : typeof data.startedAt === 'string' ? data.startedAt : undefined,
    finishedAt:
      typeof data.finished_at === 'string'
        ? data.finished_at
        : typeof data.finishedAt === 'string'
          ? data.finishedAt
          : undefined,
  };
}

export function isTerminalExtractStatus(status?: string | null): boolean {
  return typeof status === 'string' && TERMINAL_EXTRACT_STATUSES.has(status);
}

export function isTerminalExtractErrorStatus(status?: string | null): boolean {
  return typeof status === 'string' && ERROR_EXTRACT_STATUSES.has(status);
}

export const extractData = async (payload: ExtractPayload): Promise<ExtractRunSession> => {
  try {
    const response = await fetch('/api/extract', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(payload.idempotency_key ? { 'Idempotency-Key': payload.idempotency_key } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      // Read the body once (Response streams are single-use)
      const bodyText = await response.text();
      let errorBody: unknown = null;
      let parsedBodyMessage: string | null = null;
      let plainTextMessage: string | null = null;

      if (bodyText) {
        try {
          const parsed = JSON.parse(bodyText);
          errorBody = parsed;
          parsedBodyMessage = getErrorMessageFromBody(parsed);
        } catch {
          // not JSON; fall back to plain text
          plainTextMessage = bodyText.trim() || null;
        }
      }

      const fallbackMessage = response.statusText || 'Запрос завершился ошибкой';
      const safeServerMessage =
        parsedBodyMessage && SAFE_SERVER_ERROR_MESSAGES.has(parsedBodyMessage)
          ? parsedBodyMessage
          : null;
      const errorMessage =
        response.status >= 500
          ? safeServerMessage || GENERIC_EXTRACTION_ERROR_MESSAGE
          : parsedBodyMessage || plainTextMessage || fallbackMessage;

      // Surface HTTP status to the caller for better UI messaging
      throw new ApiError(errorMessage, response.status, errorBody);
    }

    const data: Record<string, unknown> = await response.json();
    return normalizeExtractRunSession(data);
  } catch (error) {
    throw error;
  }
};

export async function fetchRunStatus(runId: string): Promise<ExtractRunSession> {
  const response = await fetch(`/api/runs/${encodeURIComponent(runId)}`, {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    const bodyText = await response.text();
    let errorBody: unknown = null;
    try {
      errorBody = bodyText ? JSON.parse(bodyText) : null;
    } catch {
      errorBody = bodyText || null;
    }
    const message =
      getErrorMessageFromBody(errorBody) ||
      (typeof errorBody === 'string' && errorBody.trim().length > 0 ? errorBody.trim() : null) ||
      response.statusText ||
      'Не удалось получить статус запуска';
    throw new ApiError(message, response.status, errorBody);
  }

  const data = (await response.json()) as Record<string, unknown>;
  return normalizeExtractRunSession(data);
}

export async function issueWebsocketToken(runId: string): Promise<Pick<ExtractRunSession, 'websocketUrl' | 'websocketToken' | 'websocketTokenExpiresAt'>> {
  const response = await fetch('/api/extract/socket-token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ jobId: runId }),
  });

  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    const message =
      getErrorMessageFromBody(data) ||
      response.statusText ||
      'Не удалось получить токен WebSocket';
    throw new ApiError(message, response.status, data);
  }

  const websocketUrl = typeof data.websocketUrl === 'string' ? data.websocketUrl : null;
  if (!websocketUrl) {
    throw new ApiError('В ответе отсутствует websocketUrl', response.status, data);
  }

  return {
    websocketUrl,
    websocketToken: typeof data.token === 'string' ? data.token : undefined,
    websocketTokenExpiresAt: typeof data.expiresAt === 'number' ? data.expiresAt : undefined,
  };
}
